'use client';

import { usePathname } from 'next/navigation';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { NormalizedLocale } from '@/enums/Locale';
import { getBreadcrumb } from '@/utils/helpers/breadcrumbsHelper';
import HeaderDictionary from '@/types/dictionaries/Header';
import { useBreadcrumbs } from '@/utils/providers/BreadcrumbProvider';
import { ROUTES } from '@/constants/application-urls';
import useLocale from '@/hooks/useLocale';
import LocalizedLink from '../LocalizedLink';

const Breadcrumbs = ({ dict }: { dict: HeaderDictionary }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const path = usePathname();
	const locales = Object.values(NormalizedLocale);
	const pathNames = path.split('/').filter((segment) => !(locales as string[]).includes(segment));

	const { trailingPath } = useBreadcrumbs();

	// Exclude breadcrumb on home page and specific thank-you page
	const isHomePath = pathNames.length === 1;
	const locale = useLocale();
	const isExcludedPath = isHomePath || path.startsWith(`/${locale}${ROUTES.THANKYOU}`);
	if (isExcludedPath || isMobile) return null;

	const separator = <NavigateNextIcon sx={{ fontSize: '12px' }} data-testid="breadcrumbs-separator" />;

	return (
		<Container maxWidth="xl" sx={{ paddingTop: { sm: '20px' } }} data-testid="breadcrumbs-container">
			<MuiBreadcrumbs
				separator={separator}
				aria-label="breadcrumb"
				sx={{
					'.MuiBreadcrumbs-separator': { mx: '4px' },
				}}
			>
				{pathNames.map((link, index) => {
					const isLastLink = index === pathNames.length - 1;
					const pagePath = pathNames.slice(0, index + 1).join('/');
					const breadcrumb = getBreadcrumb(pagePath, dict);

					return isLastLink && trailingPath ? (
						<Typography
							key={link}
							color="black.main"
							variant="breadcrumb"
							sx={{ textTransform: 'capitalize' }}
							data-testid="breadcrumb-last-link"
						>
							{trailingPath}
						</Typography>
					) : (
						breadcrumb && (
							<LocalizedLink href={breadcrumb.href} underline="hover" key={breadcrumb.id} color="primary.main">
								<Typography variant="breadcrumb" sx={{ textTransform: 'capitalize' }} data-testid="breadcrumb-link">
									{breadcrumb.title}
								</Typography>
							</LocalizedLink>
						)
					);
				})}
			</MuiBreadcrumbs>
		</Container>
	);
};

export default Breadcrumbs;
